import {
  CoachCard,
  CoachFilterBar,
  groupCoachProfilesByUser,
} from '@efdigital/ll-addon-coaching';
import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import BsModal from 'ember-bootstrap/components/bs-modal';

type Signature = {
  Args: {
    /** All coach profiles; deduped to one published profile per user. */
    coaches: CoachProfile[];
    /** User ids already in the pool — their cards show a Remove action. */
    memberUserIds: string[];
    onAdd: (coach: CoachProfile) => void;
    onRemove: (userId: string) => void;
  };
  Blocks: {
    input: [showModal: () => void];
  };
};

/**
 * Modal coach browser for picking coach pool members, following the
 * ContactPicker pattern: yield a trigger, open an xl modal with search and
 * a grid of flippable CoachCards. Membership toggles apply immediately via
 * onAdd/onRemove; the modal stays open for picking several coaches.
 */
export class CoachPicker extends Component<Signature> {
  @tracked isOpen = false;

  showModal = () => {
    this.isOpen = true;
  };

  modalClosed = () => {
    this.isOpen = false;
  };

  // One card per coach: published profiles only, deduped by user.
  get publishedCoaches(): CoachProfile[] {
    return groupCoachProfilesByUser(this.args.coaches).published;
  }

  isMember = (coach: CoachProfile): boolean => {
    return Boolean(coach.user) && this.args.memberUserIds.includes(coach.user);
  };

  addableCoaches = (coaches: CoachProfile[]): CoachProfile[] => {
    return coaches.filter((coach) => coach.user && !this.isMember(coach));
  };

  addAll = (coaches: CoachProfile[]) => {
    for (const coach of this.addableCoaches(coaches)) {
      this.args.onAdd(coach);
    }
  };

  <template>
    {{yield this.showModal to="input"}}
    <BsModal
      @open={{this.isOpen}}
      @onHidden={{this.modalClosed}}
      @size="xl"
      @renderInPlace={{true}}
      @backdropClose={{false}}
      as |modal|
    >
      <modal.header>
        <h2 class="gud-eyebrow mb-2">Add coaches to pool</h2>
      </modal.header>
      <modal.body>
        <CoachFilterBar
          @coaches={{this.publishedCoaches}}
          @showAttributeFilters={{true}}
          @showDbsFilter={{true}}
          class="mb-4"
        >
          <:results as |filteredCoaches|>
            {{#if filteredCoaches.length}}
              {{#let (this.addableCoaches filteredCoaches) as |addable|}}
                {{#if addable.length}}
                  <div class="d-flex justify-content-end mb-3">
                    <button
                      type="button"
                      class="btn btn-sm btn-outline-primary"
                      data-test-add-all-to-pool
                      {{on "click" (fn this.addAll filteredCoaches)}}
                    >
                      <iconify-icon
                        inline
                        icon="ph:user-circle-plus"
                        aria-hidden="true"
                      />
                      Add all shown ({{addable.length}})
                    </button>
                  </div>
                {{/if}}
              {{/let}}

              <div class="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-4">
                {{#each filteredCoaches as |coach|}}
                  <div class="col">
                    <CoachCard @coach={{coach}} @showDbsBadge={{true}}>
                      <:actions>
                        {{#if (this.isMember coach)}}
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-danger"
                            data-test-remove-from-pool
                            {{on "click" (fn @onRemove coach.user)}}
                          >
                            <iconify-icon
                              inline
                              icon="ph:check-circle"
                              aria-hidden="true"
                            />
                            In pool — remove
                          </button>
                        {{else if coach.user}}
                          <button
                            type="button"
                            class="btn btn-sm btn-primary"
                            data-test-add-to-pool
                            {{on "click" (fn @onAdd coach)}}
                          >
                            Add to pool
                          </button>
                        {{else}}
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-primary"
                            disabled
                            title="This coach has no linked platform account"
                          >
                            No linked account
                          </button>
                        {{/if}}
                      </:actions>
                    </CoachCard>
                  </div>
                {{/each}}
              </div>
            {{else}}
              <div class="text-center py-5 text-body-secondary">
                <iconify-icon
                  inline
                  icon="ph:magnifying-glass"
                  class="fs-1 mb-3"
                  aria-hidden="true"
                />
                <p class="mb-0">No coaches match your search.</p>
              </div>
            {{/if}}
          </:results>
        </CoachFilterBar>
      </modal.body>
      <modal.footer class="text-center">
        <button
          type="button"
          class="btn btn-outline-secondary"
          {{on "click" this.modalClosed}}
        >
          Done
        </button>
      </modal.footer>
    </BsModal>
  </template>
}

export default CoachPicker;
